import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Fade,
  Zoom
} from '@mui/material';
import {
  Business as BusinessIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Add as AddIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GetAll, apiURL } from '../../../utils/apiCalls';
import { toast } from 'react-toastify';
import { filter } from 'lodash';

const TABLE_HEAD = [
  { id: 'no', label: 'SNo.', align: 'left' },
  // { id: 'venture_name', label: 'Venture Name', align: 'left' },
  { id: 'company_name', label: 'Company Name', align: 'left' },
  // { id: 'location', label: 'Location', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'created_at', label: 'Requested On', align: 'left' },
  { id: 'actions', label: 'Actions', align: 'center' },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => 
      _user.venture_name?.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
      _user.company_name?.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

const ProcurementLayoutRequests = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [list, setList] = useState([]);

  async function GetList() {
    try {
      const response = await GetAll(apiURL + '/api/v1/layouts/my/requests/filter');
      if (response && response.data && response.data.status) {
        setList(response.data.data);
      } else {
        toast.error(response.data.message);
        setList([]);
      }
    } catch (error) {
      console.error('Error fetching layout requests:', error);
      toast.error('An error occurred while fetching layout requests');
    }
  }

  useEffect(() => {
    GetList();
  }, []);



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - list.length) : 0;

  const filteredUsers = applySortFilter(list, getComparator('asc', 'id'), filterName);

  const isUserNotFound = filteredUsers.length === 0;

  const handleViewClick = (id) => {
    navigate(`/dashboard/procurement/layout-requests/view/${id}`);
  };

  const handleCreateRequest = () => {
    // Get QR code from user details or generate one
    const userDetails = JSON.parse(sessionStorage.getItem('user_details') || '{}');
    const qr = userDetails.id || 'default';
    navigate(`/dashboard/LayoutOnboardingRequest/${qr}`);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'success';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return <CheckCircleIcon />;
      case 'approved': return <CheckCircleIcon />;
      case 'rejected': return <CancelIcon />;
      case 'pending': return <PendingIcon />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container sx={{ m: 0, width: '100%', maxWidth: '100%' }} maxWidth={false}>
      {/* Header Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Avatar sx={{
              bgcolor: '#1976d2',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}>
              <BusinessIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" color="text.primary">
                My Layout Requests
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Manage your layout onboarding requests
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Fade>

      {/* Action Bar */}
      <Zoom in timeout={1000}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
              <TextField
                placeholder="Search by venture name or company name..."
                value={filterName}
                onChange={handleFilterByName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 400 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateRequest}
                sx={{ minWidth: 180 }}
              >
                New Layout Request
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Zoom>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Zoom in timeout={1200}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                    <BusinessIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Requests
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Zoom in timeout={1400}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40 }}>
                    <PendingIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => item.status === 'pending').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Zoom in timeout={1600}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: '#2196f3', width: 40, height: 40 }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => item.status === 'approved').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Approved
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Zoom in timeout={1800}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => item.status === 'paid').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Paid
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Zoom in timeout={2000}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: '#f44336', width: 40, height: 40 }}>
                    <CancelIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => item.status === 'rejected').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rejected
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Requests Table */}
      <Zoom in timeout={2000}>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.align}
                    >
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="left">
                        <Typography variant="body2" color="text.secondary">
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      {/* <TableCell align="left">
                        <Typography variant="body1" fontWeight="bold">
                          {row.venture_name}
                        </Typography>
                      </TableCell> */}
                      <TableCell align="left">
                        <Typography variant="body2" color="text.secondary">
                          {row.company_name}
                        </Typography>
                      </TableCell>
                      {/* <TableCell align="left">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {row.location_latitude}, {row.location_longitude}
                          </Typography>
                        </Stack>
                      </TableCell> */}
                      <TableCell align="left">
                        <Chip
                          label={row.status || 'Pending'}
                          color={getStatusColor(row.status)}
                          icon={getStatusIcon(row.status)}
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(row.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              color="primary"
                              onClick={() => handleViewClick(row.id)}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={7} />
                  </TableRow>
                )}
              </TableBody>

              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                      <Typography gutterBottom align="center" variant="subtitle1">
                        No layout requests found
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateRequest}
                        sx={{ mt: 2 }}
                      >
                        Create Your First Request
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={list.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Zoom>
    </Container>
  );
};

export default ProcurementLayoutRequests;
